import { consent_api, customer_api } from '@axo/shared/data-access/api';
import { customer, consent } from '@axo/shared/data-access/types';
import { Handler } from '../api-orchestrator';
import { IPreconditions } from '../types/IPreconditions';

interface IDependencies {
  patchCustomer: typeof customer_api.patchCustomer;
}

export const marketingConsentHandlerFactory =
  ({
    patchCustomer,
  }: IDependencies): Handler<
    customer.UpdateCustomer & {
      CoApplicantMarketingConsent?: customer.MarketingConsent;
    } & consent.ConsentConfig,
    IPreconditions
  > =>
  async ({
    data,
    preconditions: {
      customerJWT,
      coCustomerJWT,
      customerId,
      coCustomerId,
      loanApplicationId,
      // customerHasConsentedToMarketing flag is initially set in reloadState.ts, to determine if the customer has already consented to a marketing consent
      customerHasConsentedToMarketing,
      coCustomerHasConsentedToMarketing,
      brand,
    },
    baseUrl,
  }) => {
    if (!customerJWT || !customerId || !loanApplicationId)
      return { didSync: false };

    const hasMarketingConsentData =
      data.MarketingConsent && Object.keys(data.MarketingConsent).length > 0;

    const hasCoAppMarketingConsentData =
      data.CoApplicantMarketingConsent &&
      Object.keys(data.CoApplicantMarketingConsent).length > 0;

    if (!hasMarketingConsentData && !hasCoAppMarketingConsentData) {
      return {
        didSync: true,
        newPreconditions: {
          customerHasConsentedToMarketing: false,
          coCustomerHasConsentedToMarketing: false,
        },
      };
    }

    const masterConsentData = {
      Country: data.Config?.Country ?? '',
      ConfigId: data.Config?.ConfigId ?? 0,
      OriginBrandID: brand ?? '',
      OriginAction: data.Config?.OriginAction ?? '',
      Lang: data.Config?.Lang ?? '',
    };

    if ('CoApplicantMarketingConsent' in data) {
      if (!coCustomerJWT || !coCustomerId) return { didSync: false };

      if (coCustomerHasConsentedToMarketing) return { didSync: false };

      const api = { url: { service: baseUrl }, token: coCustomerJWT };

      if (data.Config) {
        await consent_api.sendMasterConsent(api)({
          ...masterConsentData,
          CustomerID: coCustomerId,
        });
      } else {
        await patchCustomer(api)(coCustomerId, {
          MarketingConsent: data.CoApplicantMarketingConsent,
        });
      }
      return {
        didSync: true,
        newPreconditions: { coCustomerHasConsentedToMarketing: true },
      };
    }

    if (customerHasConsentedToMarketing) {
      return { didSync: false };
    }

    const api = { url: { service: baseUrl }, token: customerJWT };

    if (data.Config) {
      await consent_api.sendMasterConsent(api)({
        ...masterConsentData,
        CustomerID: customerId,
      });
    } else {
      await patchCustomer(api)(customerId, {
        MarketingConsent: data.MarketingConsent,
      });
    }
    return {
      didSync: true,
      newPreconditions: { customerHasConsentedToMarketing: true },
    };
  };
